import { Button, Spin, Tooltip } from "antd";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import OverviewStats from "./OverviewStats";
import moment from "moment";
import { HistoryOutlined, WarningOutlined } from "@ant-design/icons";
import AddTopPlanModal from "../modal/add/AddTopPlanModal";
import ConfirmationModal from "../modal/ConfirmationModal";
import ActivePerfCycleApi from "../../api/ActivePerfCycleApi";
import CompletePerfCycleApi from "../../api/CompletePerfCycleApi";
import {
  openNotificationError,
  openNotificationSuccess,
} from "../../helpers/NotificationHelpers";
import { perfCycleDetails } from "../../features/PerfCycleSlice";
import GetPerfCycleApi from "../../api/GetPerfCycleApi";
import PerfCycleTimelineModal from "../modal/info/PerfCycleTimelineModal";
import { permissionAbility } from "../../helpers/permissionHelpers";
import PlanGroupStats from "./PlanGroupStats";
import PlanLevelStats from "./PlanLevelStats";
import PerformanceStats from "./PerformanceStats";
import BestWorstTopPlans from "./BestWorstTopPlans";

function CurrentCycle() {
  const [addModal, setAddModal] = useState(false);
  const [timelineModal, setTimelineModal] = useState(false);
  const [confirmModal, setConfirmModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [confirmType, setConfirmType] = useState("");

  const [planAdd, setPlanAdd] = useState(false);
  const userState = useSelector((state) => state.userState.value);
  const perfCycleState = useSelector((state) => state.perfCycleState.value);

  const positionState = useSelector((state) => state.positionState.value);
  const userLevel = positionState?.userRole?.name;
  const proHeight = document.body.clientHeight;
  const dispatch = useDispatch();

  async function changeStatus() {
    let apiResponse = {};
    if (confirmType === "activate") {
      apiResponse = await ActivePerfCycleApi(
        perfCycleState.id,
        positionState?.id
      );
    } else if (confirmType === "complete") {
      apiResponse = await CompletePerfCycleApi(
        perfCycleState.id,
        positionState?.id
      );
    } else if (confirmType === "reactivate") {
      apiResponse = await ActivePerfCycleApi(
        perfCycleState.id,
        positionState?.id
      );
    }

    if (apiResponse.statusCode) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse.message === "success") {
      openNotificationSuccess("bottomRight", `Perf cycle ${confirmType}ed`);
      /*As value is object we had to empty it to rerender */
      dispatch(perfCycleDetails({}));
      dispatch(perfCycleDetails(apiResponse.performanceCycle));
    } else {
      openNotificationError(
        "bottomRight",
        "Couldn't complete. Try again later."
      );
    }
    setConfirmType("");
  }

  function getTimingText() {
    let output = "";
    if (moment(perfCycleState?.startingOn) > moment()) {
      output = `This performance cycle starts ${moment(
        perfCycleState?.startingOn
      )
        .endOf("day")
        .fromNow()}`;
    } else if (moment(perfCycleState?.endingOn) < moment()) {
      output = `This performance cycle ended ${moment(perfCycleState?.endingOn)
        .endOf("day")
        .fromNow()}`;
    } else {
      output = `This performance cycle ends ${moment(perfCycleState?.endingOn)
        .endOf("day")
        .fromNow()}`;
    }

    return output;
  }

  async function planAdded() {
    if (!perfCycleState.havePlans) {
      setLoading(true);
      let apiResponse = await GetPerfCycleApi(perfCycleState.id);
      if (apiResponse.message === "success") {
        /*As value is object we had to empty it to rerender */
        dispatch(perfCycleDetails({}));
        dispatch(perfCycleDetails(apiResponse.performanceCycle));
      }
      setLoading(false);
    }
    /*To rerender overview stats */
    setPlanAdd(!planAdd);
  }

  return (
    <>
      <div className="flex justify-between">
        <div className="mb-3">
          <div className="flex items-center">
            <div
              className={
                "w-2 h-2 rounded-full mr-1 " +
                (perfCycleState?.status === "Active"
                  ? "bg-green-500"
                  : perfCycleState?.status === "Scheduled"
                  ? "bg-orange-500"
                  : "bg-blue-500")
              }
            />
            <div className="text-base">{getTimingText()}</div>
          </div>
        </div>
        <div className="inline">
          {permissionAbility(userLevel, "perf-cycles", "manage") && (
            <div className="flex items-center">
              {perfCycleState?.status === "Active" ? (
                <Button
                  danger={moment(perfCycleState?.endingOn) < moment(new Date())}
                  onClick={() => {
                    setConfirmType("complete");
                    setConfirmModal(true);
                  }}
                >
                  Complete
                </Button>
              ) : perfCycleState?.status === "Scheduled" ? (
                <Button
                  type={
                    moment(perfCycleState?.startingOn) < moment(new Date()) &&
                    "primary"
                  }
                  onClick={() => {
                    setConfirmType("activate");
                    setConfirmModal(true);
                  }}
                >
                  Activate
                </Button>
              ) : (
                <Button
                  onClick={() => {
                    setConfirmType("reactivate");
                    setConfirmModal(true);
                  }}
                >
                  Reactivate
                </Button>
              )}
              <Tooltip title={"Timeline"}>
                <div
                  onClick={() => setTimelineModal(true)}
                  className="ml-2 text-blue-500 hover:text-blue-600 cursor-pointer"
                >
                  <HistoryOutlined />
                </div>
              </Tooltip>
            </div>
          )}
        </div>
      </div>
      {perfCycleState?.havePlans ? (
        <>
          <div className="mt-10">
            <BestWorstTopPlans />
          </div>
        </>
      ) : (
        <Spin spinning={loading}>
          <div
            style={{ height: proHeight - 180 }}
            className={"flex w-full items-center justify-center"}
          >
            {perfCycleState?.status != "Completed" ? (
              <div className="text-center">
                <div className="text-3xl text-gray-400">
                  <WarningOutlined />
                </div>
                <div className="text-lg pri-font text-gray-400">
                  No plans available
                </div>
                <div className="mb-3 text-gray-400 text-sm">
                  Add plans to performance cycle
                </div>
                <Button onClick={() => setAddModal(true)} type="dashed">
                  Add plans
                </Button>
              </div>
            ) : (
              <div className="text-center">
                <div className="text-3xl text-gray-400">
                  <WarningOutlined />
                </div>
                <div className="text-lg pri-font text-gray-400">
                  This performance cycle is ended
                </div>
              </div>
            )}
          </div>
        </Spin>
      )}

      <AddTopPlanModal
        visible={addModal}
        handleCancel={() => setAddModal(false)}
        handleOk={() => planAdded()}
      />
      <ConfirmationModal
        visible={confirmModal}
        handleCancel={() => setConfirmModal(false)}
        handleOk={() => {
          changeStatus();
          setConfirmModal(false);
        }}
        text={`Are you sure want to ${confirmType} this cycle?`}
        title={`${
          confirmType.charAt(0).toUpperCase() + confirmType.slice(1)
        } cycle`}
      />
      <PerfCycleTimelineModal
        visible={timelineModal}
        handleCancel={() => setTimelineModal(false)}
      />
    </>
  );
}

export default CurrentCycle;
